import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import { reconcileUnpaidYears } from '../../services/esetApi';
import type { EsetReconcileResult, EsetUnpaidRow, ReconcileAction } from '../../services/esetApi';

interface EsetReconcileDialogProps {
  row: EsetUnpaidRow | null; // null = closed
  onClose: () => void;
  /** Called after a REAL run completes, so the table can refresh. */
  onReconciled?: () => void;
}

function fmt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Per-year Bill / Waive segmented toggle (info tone = bill, amber tone = waive). */
const Segmented: React.FC<{
  value: ReconcileAction;
  onChange: (a: ReconcileAction) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <Box sx={{ display: 'inline-flex', border: '0.5px solid #d0dcea', borderRadius: '6px', overflow: 'hidden' }}>
    {(['bill', 'waive'] as const).map((a) => {
      const active = value === a;
      const tone = a === 'bill' ? { bg: '#E6F1FB', fg: '#0C447C' } : { bg: '#FFFBF0', fg: '#C07A10' };
      return (
        <Box
          component="button"
          type="button"
          key={a}
          disabled={disabled}
          onClick={() => onChange(a)}
          sx={{
            border: 'none',
            cursor: disabled ? 'default' : 'pointer',
            px: 1.25,
            py: 0.5,
            fontSize: 12,
            fontWeight: 500,
            background: active ? tone.bg : '#ffffff',
            color: active ? tone.fg : '#888888',
            opacity: disabled ? 0.5 : 1,
            '&:hover': active ? {} : { background: '#f5f7fa' },
          }}
        >
          {a === 'bill' ? 'Bill' : 'Waive'}
        </Box>
      );
    })}
  </Box>
);

function resultMessage(r: EsetReconcileResult): string {
  const b = r.billed ?? 0;
  const w = r.waived ?? 0;
  switch (r.status) {
    case 'dry_run':
      return `Dry-run: ${b} to bill, ${w} to waive — nothing written.`;
    case 'reconciled':
      return `Done — ${b} billed, ${w} waived${r.invoice_id ? ` (invoice ${r.invoice_id})` : ''}.`;
    case 'billing_disabled':
      return `ENABLE_BILLING is off — nothing written (${b} would bill, ${w} would waive).`;
    case 'none':
      return 'Nothing to reconcile — these years are already handled.';
    default:
      return `No action: ${r.status}.`;
  }
}

/** Reconcile a subscription's never-billed years: choose Bill (full price) or Waive ($0)
 *  per year, preview with a dry-run, then confirm the real run (gated by ENABLE_BILLING). */
export const EsetReconcileDialog: React.FC<EsetReconcileDialogProps> = ({ row, onClose, onReconciled }) => {
  const periods = useMemo(() => row?.periods ?? [], [row]);
  const [actions, setActions] = useState<Record<string, ReconcileAction>>({});
  const [dryRun, setDryRun] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<EsetReconcileResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // (Re)initialise every year to "bill" each time the dialog opens (row cycles null -> row).
  useEffect(() => {
    if (!row) return;
    setActions(Object.fromEntries((row.periods ?? []).map((p) => [p.start, 'bill' as ReconcileAction])));
    setDryRun(true);
    setConfirming(false);
    setResult(null);
    setError(null);
  }, [row]);

  if (!row) return null;

  const billed = periods.filter((p) => actions[p.start] === 'bill').length;
  const waived = periods.length - billed;

  const setAll = (a: ReconcileAction) => {
    setActions(Object.fromEntries(periods.map((p) => [p.start, a])));
    setConfirming(false);
  };

  const close = () => {
    onClose();
  };

  const execute = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await reconcileUnpaidYears(row.id, { decisions: actions, dry_run: dryRun });
      setResult(res);
      if (!dryRun && res.status === 'reconciled') onReconciled?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reconcile failed');
    } finally {
      setRunning(false);
      setConfirming(false);
    }
  };

  const onRunClick = () => {
    if (!dryRun && !confirming) {
      setConfirming(true); // real run needs a second, explicit confirm
      return;
    }
    execute();
  };

  const runLabel = dryRun
    ? 'Preview (dry-run)'
    : confirming
      ? 'Confirm — apply now'
      : 'Apply for real';

  return (
    <Dialog open onClose={running ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Reconcile unpaid years
        <Box sx={{ fontSize: 13, fontWeight: 400, color: '#888', mt: 0.25 }}>
          {row.customer_name || '—'} · {row.product_bundle || '—'}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: 13, color: '#555', mb: 1 }}>
          {periods.length} year{periods.length === 1 ? '' : 's'} never billed. Choose <strong>Bill</strong>{' '}
          (charge the usual price) or <strong>Waive</strong> ($0, marked "previously omitted") for each —
          all on one invoice, with a note per line and an entry on the ESET history.
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Button size="small" onClick={() => setAll('bill')} disabled={running}
            sx={{ fontSize: 12, color: '#4A90D9', minWidth: 0 }}>
            Bill all
          </Button>
          <Button size="small" onClick={() => setAll('waive')} disabled={running}
            sx={{ fontSize: 12, color: '#C07A10', minWidth: 0 }}>
            Waive all
          </Button>
        </Box>

        <Box sx={{ border: '0.5px solid #e2eaf3', borderRadius: '6px', overflow: 'hidden' }}>
          {periods.map((p) => (
            <Box
              key={p.start}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                borderTop: '0.5px solid #eef2f7', '&:first-of-type': { borderTop: 'none' },
              }}
            >
              <Box sx={{ flex: 1, fontSize: 13, color: '#333' }}>
                Covers {fmt(p.start)} – {fmt(p.end)}
              </Box>
              <Segmented
                value={actions[p.start] ?? 'bill'}
                onChange={(a) => { setActions((s) => ({ ...s, [p.start]: a })); setConfirming(false); }}
                disabled={running}
              />
            </Box>
          ))}
        </Box>

        <Box sx={{ fontSize: 12, color: '#646464', mt: 1 }}>
          {billed} to bill · {waived} to waive
        </Box>

        <FormControlLabel
          sx={{ mt: 0.5 }}
          control={
            <Checkbox
              checked={dryRun}
              disabled={running}
              onChange={(e) => { setDryRun(e.target.checked); setConfirming(false); }}
            />
          }
          label="Dry run (preview, no writes)"
        />

        {!dryRun && billed > 0 && (
          <Alert severity="warning" sx={{ mt: 0.5 }}>
            Real run — this charges {row.customer_name || 'the customer'} for {billed} year
            {billed === 1 ? '' : 's'} at the usual price (needs ENABLE_BILLING).
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        {result && (
          <Alert
            severity={result.dry_run ? 'info' : result.status === 'reconciled' ? 'success' : 'warning'}
            sx={{ mt: 1.5 }}
          >
            {resultMessage(result)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={running} sx={{ color: '#aaa' }}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={onRunClick}
          disabled={running || periods.length === 0}
          color={!dryRun && confirming ? 'warning' : 'primary'}
        >
          {runLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
